import {useQuery} from '@tanstack/react-query';
import {Chess} from 'chess.js';
import Environment from '../../../environment';
import {buildLichessAuthHeaders, ExplorerDb} from './useOpeningExplorer';

/** One ply of a replayed game, with the position immediately after it. */
export type GamePly = {
  san: string;
  fen: string;
};

export type ExplorerGamePgn = {
  startFen: string;
  plies: GamePly[];
};

/**
 * Fetches a single game's PGN by id from the same opening-explorer database
 * it was found in, then replays it into a per-ply FEN list for a board
 * viewer. `/pgn/{id}` is the same shape on both our self-hosted masters
 * instance and lichess's own explorer, so only the base URL differs.
 */
function buildPgnUrl(gameId: string, db: ExplorerDb): string {
  if (db === 'masters') {
    const mastersBaseUrl =
      (Environment as any).EXPLORER_MASTERS_BASE_URL ||
      'https://explorer.lichess.ovh/masters';
    return `${mastersBaseUrl}/pgn/${gameId}`;
  }
  return `https://explorer.lichess.ovh/lichess/pgn/${gameId}`;
}

function parsePlies(pgn: string): ExplorerGamePgn {
  const chess = new Chess();
  chess.loadPgn(String(pgn).replace(/\r/g, '').trim(), {strict: false} as any);
  const history = chess.history({verbose: true}) as any[];
  return {
    startFen: history[0]?.before || chess.header()?.FEN ||
      'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    plies: history.map(mv => ({san: mv.san, fen: mv.after})),
  };
}

export default function useExplorerGamePgn(
  gameId: string | undefined | null,
  db: ExplorerDb,
  enabled = true,
) {
  return useQuery<ExplorerGamePgn, Error>(
    ['explorerGamePgn', db, gameId],
    async ({signal}) => {
      const res = await fetch(buildPgnUrl(gameId as string, db), {
        signal,
        headers: buildLichessAuthHeaders(db),
      });
      if (!res.ok) {
        throw new Error(`Failed to load game (${res.status})`);
      }
      const pgn = await res.text();
      return parsePlies(pgn);
    },
    {
      enabled: enabled && !!gameId,
      staleTime: Infinity,
      retry: 1,
    },
  );
}
